import { memo, useCallback, useMemo, useState } from "react"
import ReactDOM from "react-dom"
import { useDispatch, useSelector } from "react-redux"

import { caseQuickUpdate, linkSurgeryFromService } from "../../../../lib/fetchApi"
import { atpProvidersOptions, insideClinicOptions, outsideClinicOptions } from "../../../../lib/systemData"
import { serviceNeedsSurgeryLink } from "../../../../lib/misc"
import Button from "../../../common/Button"
import CheckboxCell from "../../../common/interactive/CheckboxCell"
import CustomSelect from "../../../common/interactive/CustomSelect"
import InputBox from "../../../common/interactive/InputBox"
import DateDisplayWithNmuberOfDays from "../../../common/DateDisplayWithNmuberOfDays"

import classes from "./ServicesItem.module.css"

const LINK_SURGERY_TOOLTIP = "Create surgery"

const ServicesItem = ({itemData, surgeries, servicesEditDisabled}) => {

    const dispatch = useDispatch()

    const activeCase = useSelector(state => state.case.activeCase)    
    const userToken = useSelector(state => state.user.token)

    const [isLinkingSurgery, setIsLinkingSurgery] = useState(false)
    const [linkError, setLinkError] = useState(null)
    const [showLinkTooltip, setShowLinkTooltip] = useState(false)
    const [showLinkConfirm, setShowLinkConfirm] = useState(false)

    const showLinkSurgery = useMemo(
        () => serviceNeedsSurgeryLink(itemData, surgeries),
        [itemData, surgeries]
    )

    const updateHandlerAlternative = useCallback(async (data) => {
        if (!activeCase?.case?.id) {
            return
        }
        let reqBody = {
            type: data.identifier.type,
            type_id: data.identifier.type_id,
            fields: {
                [data.identifier.field] : data.data.value,
            }
        }
        await caseQuickUpdate(userToken, activeCase.case.id, reqBody, dispatch)    
    }, [activeCase?.case?.id, dispatch, userToken])

    const executeLinkSurgery = useCallback(async () => {
        if (!activeCase?.case?.id || servicesEditDisabled || isLinkingSurgery) {
            return
        }

        setIsLinkingSurgery(true)
        setLinkError(null)

        const response = await linkSurgeryFromService(
            userToken,
            activeCase.case.id,
            itemData.id,
            dispatch
        )

        setIsLinkingSurgery(false)

        if (response?.errorMessage) {
            const errors = response.errorMessage
            const message = typeof errors === 'string'
                ? errors
                : (errors?.service?.[0] || errors?.errors || 'Unable to create surgery.')
            setLinkError(message)
            return
        }

        if (response?.staleResponse) {
            return
        }

        setLinkError(null)
    }, [activeCase?.case?.id, dispatch, isLinkingSurgery, itemData.id, servicesEditDisabled, userToken])

    const openLinkConfirmHandler = useCallback((event) => {
        event.preventDefault()
        event.stopPropagation()

        if (!activeCase?.case?.id || servicesEditDisabled || isLinkingSurgery) {
            return
        }

        setShowLinkConfirm(true)
    }, [activeCase?.case?.id, isLinkingSurgery, servicesEditDisabled])

    const closeLinkConfirmHandler = useCallback(() => {
        if (!isLinkingSurgery) {
            setShowLinkConfirm(false)
        }
    }, [isLinkingSurgery])

    const confirmLinkSurgeryHandler = useCallback(async () => {
        setShowLinkConfirm(false)
        await executeLinkSurgery()
    }, [executeLinkSurgery])

    const providers = useMemo(() => atpProvidersOptions(), [])
    const outsideClinics = useMemo(() => outsideClinicOptions(), [])
    const insideClinics = useMemo(() => insideClinicOptions(), [])
    const providerLabel = useMemo(() => {
        return providers?.find(item => item.value == itemData.order_by_id)?.label
    }, [providers, itemData.order_by_id])

    const overlayRoot = typeof document !== "undefined" ? document.getElementById("overlay-root") : null

    return <tr key={itemData.id}>
        <td >
            <div className={classes.date_ordered_cell}>
                {showLinkSurgery && (
                    <span className={classes.link_surgery_wrap}>
                        <button
                            type="button"
                            className={classes.link_surgery_button}
                            disabled={servicesEditDisabled || isLinkingSurgery}
                            onClick={openLinkConfirmHandler}
                            onPointerEnter={() => setShowLinkTooltip(true)}
                            onPointerLeave={() => setShowLinkTooltip(false)}
                            aria-label={LINK_SURGERY_TOOLTIP}
                        >
                            <i className="ti ti-square-plus"></i>
                        </button>
                        {showLinkTooltip && (
                            <span className={classes.link_surgery_tooltip} role="tooltip">
                                {LINK_SURGERY_TOOLTIP}
                            </span>
                        )}
                    </span>
                )}
                <DateDisplayWithNmuberOfDays date={itemData.date_ordered} textRight={true}/>
            </div>
            {linkError && <div className={classes.date_ordered_cell_error}>{linkError}</div>}
        </td>
        <td>
            {itemData.order_by_id && <>
                {providerLabel}
            </>}
        </td>

        <td>{itemData.class}</td>

        <td style={{paddingTop: 2, paddingBottom: 2}}>{itemData.description}</td>

        <td>
            <CheckboxCell checked={itemData.is_outside_clinic} action={updateHandlerAlternative} identifier={{type: 'case_services', field: 'is_outside_clinic', type_id: itemData.id, folder: 'services'}} setStyle={'centered'}/>
        </td>

        <td className="table-cell-no-padding">
            {itemData.is_outside_clinic ? 
            <CustomSelect optionsData={outsideClinics} preSelectedValue={itemData?.outside_location_id} action={updateHandlerAlternative} identifier={{type: 'case_services', field: 'outside_location_id', type_id: itemData.id, folder: 'services'}} sameSizeAsContainer={true} hasRemove={true}/>
            :
            <CustomSelect optionsData={insideClinics} preSelectedValue={itemData?.practice_id} action={updateHandlerAlternative} identifier={{type: 'case_services', field: 'practice_id', type_id: itemData.id, folder: 'services'}} sameSizeAsContainer={true} hasRemove={true}/>
            }
           
        </td>

        <td>
            {itemData.is_outside_clinic ?
                <InputBox bkTransparent={true} currentValue={itemData?.outside_provider} action={updateHandlerAlternative} identifier={{type: 'case_services', field: 'outside_provider', type_id: itemData.id, folder: 'services'}} setStyle={'simple'}/>
                :
                <CustomSelect optionsData={providers} preSelectedValue={itemData?.provider_id} action={updateHandlerAlternative} identifier={{type: 'case_services', field: 'provider_id', type_id: itemData.id, folder: 'services'}} sameSizeAsContainer={true} hasRemove={true}/>
        
            }
            
        </td>

        <td>
            <DateDisplayWithNmuberOfDays date={itemData.completed_date} textRight={true}/>     
        </td>

        {showLinkConfirm && overlayRoot && ReactDOM.createPortal(
            <div
                className={classes.confirm_overlay}
                role="presentation"
                onClick={closeLinkConfirmHandler}
            >
                <div
                    className={classes.confirm_card}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={`link-surgery-confirm-${itemData.id}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h3 id={`link-surgery-confirm-${itemData.id}`} className={classes.confirm_title}>
                        Create surgery?
                    </h3>
                    <p className={classes.confirm_text}>
                        Create and link a surgery record for this service?
                    </p>
                    <div className={classes.confirm_actions}>
                        <Button click={closeLinkConfirmHandler} btnStyle="btn_white" isDisabled={isLinkingSurgery}>
                            Cancel
                        </Button>
                        <Button click={confirmLinkSurgeryHandler} isLoading={isLinkingSurgery}>
                            Create surgery
                        </Button>
                    </div>
                </div>
            </div>,
            overlayRoot
        )}
    </tr>
}

export default memo(ServicesItem)
